import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LayoutMasterDetail from "layouts/LayoutMasterDetail";

import { AuthContext } from "shared/context/auth-context";
import { PERMISSION_TYPES } from "@routebot/constants";

import { StoreContext } from "shared/context/store-context";

import InventoryStockItem from "./managed-stocks/InventoryStockItem";
// import StockStats from "./managed-stocks/components/StockStats";
// import LowStockAlert from "./managed-stocks/components/LowStockAlert";

import StockTabs from "./managed-stocks/StockTabs";

import "./InventoryStocksList.css";

const InventoryStocks = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const itemType = "stock";
  const listName = "inventory_stocks";

  const { stocks, InsertStock, UpdateStock, DeleteStock } = useContext(StoreContext);

  // const [showStats, setShowStats] = useState(false);
  const [pageItems, setPageItems] = useState();

  useEffect(() => {
    setPageItems(stocks);
  }, [stocks]);

  // Stock status badge
  const getStatusBadge = (status) => {
    let statusText = "";
    let statusClass = "";

    switch (status) {
      case "active":
        statusText = t(`inventoryStock.status.${status}`);
        statusClass = "status-active";
        break;
      case "low_stock":
        statusText = t(`inventoryStock.status.${status}`);
        statusClass = "status-low";
        break;
      case "out_of_stock":
        statusText = t(`inventoryStock.status.${status}`);
        statusClass = "status-out";
        break;
      case "discontinued":
        statusText = t(`inventoryStock.status.${status}`);
        statusClass = "status-discontinued";
        break;
      case "reserved":
        statusText = t(`inventoryStock.status.${status}`);
        statusClass = "status-reserved";
        break;
      default:
        statusText = status;
        statusClass = "status-default";
    }

    return <span className={`status-badge ${statusClass}`}>{statusText}</span>;
  };

  // Helper function to convert stock units to Turkish
  const getStockUnitsInTurkish = (unit) => {
    const unitCanonicalMap = {
      // Temel birimler
      piece: "piece",
      pieces: "piece",
      pcs: "piece",
      unit: "unit",
      units: "unit",

      // Ağırlık birimleri
      kg: "kg",
      kilogram: "kg",
      kilograms: "kg",
      g: "gram",
      gram: "gram",
      grams: "gram",
      ton: "ton",
      tons: "ton",
      lb: "lbs",
      lbs: "lbs",
      pound: "lbs",
      pounds: "lbs",

      // Hacim birimleri
      l: "liter",
      liter: "liter",
      liters: "liter",
      ml: "ml",
      milliliter: "ml",
      milliliters: "ml",
      m3: "m3",
      cubic_meter: "m3",
      cubic_meters: "m3",

      // Uzunluk birimleri
      m: "meter",
      meter: "meter",
      meters: "meter",
      cm: "cm",
      centimeter: "cm",
      centimeters: "cm",
      mm: "mm",
      millimeter: "mm",
      millimeters: "mm",
      km: "km",
      kilometer: "km",
      kilometers: "km",
      inch: "inch",
      inches: "inch",
      ft: "feet",
      feet: "feet",

      // Alan birimleri
      m2: "m2",
      square_meter: "m2",
      square_meters: "m2",
      cm2: "cm2",
      square_centimeter: "cm2",
      square_centimeters: "cm2",

      // Paket birimleri
      box: "box",
      boxes: "box",
      pack: "pack",
      packs: "pack",
      package: "pack",
      packages: "pack",
      bundle: "bundle",
      bundles: "bundle",
      roll: "roll",
      rolls: "roll",
      set: "set",
      sets: "set",
      pair: "pair",
      pairs: "pair",

      // Zaman birimleri
      hour: "hour",
      hours: "hour",
      day: "day",
      days: "day",
      week: "week",
      weeks: "week",
      month: "month",
      months: "month",
      year: "year",
      years: "year",

      // Diğer birimler
      bottle: "bottle",
      bottles: "bottle",
      can: "can",
      cans: "can",
      bag: "bag",
      bags: "bag",
      container: "container",
      containers: "container",
      pallet: "pallet",
      pallets: "pallet",
      carton: "carton",
      cartons: "carton",
    };

    // Önce tam eşleşme ara
    const canonical = unitCanonicalMap[unit.toLowerCase()];
    if (canonical) {
      return t(`inventoryStock.units.${canonical}`);
    }

    // Eğer bulunamazsa orijinal birimi döndür
    return unit;
  };

  const getDepoLabel = (depoValue) => {
    return t(`inventoryStock.depo.${depoValue}`) || depoValue;
  };

  const listColumns = [
    {
      field: "stockNumber",
      headerName: t("inventoryStock.columns.stockNumber"),
      width: 180,
      renderCell: (params) => <span className="stock-number">{params.value}</span>,
    },
    {
      field: "name",
      headerName: t("inventoryStock.columns.name"),
      width: 250,
      renderCell: (params) => (
        <div className="stock-name">
          <strong>{params.value}</strong>
          {params.row.description && <div className="stock-description">{params.row.description}</div>}
        </div>
      ),
    },
    {
      field: "depo",
      headerName: t("inventoryStock.columns.depo"),
      width: 180,
      renderCell: (params) => getDepoLabel(params.value),
    },
    {
      field: "currentQuantity",
      headerName: t("inventoryStock.columns.currentQuantity"),
      width: 130,
      renderCell: (params) => (
        <div className="quantity-cell">
          <span className={params.value === 0 ? "quantity-zero" : "quantity-normal"}>{params.value}</span>
          <span className="unit">{getStockUnitsInTurkish(params.row.unit)}</span>
        </div>
      ),
    },
    {
      field: "status",
      headerName: t("inventoryStock.columns.status"),
      width: 130,
      renderCell: (params) => getStatusBadge(params.value),
    },
    {
      field: "unitPrice",
      headerName: t("inventoryStock.columns.unitPrice"),
      width: 120,
      renderCell: (params) => <span>{params.value ? `₺${params.value.toFixed(2)}` : "-"}</span>,
    },
    {
      field: "totalValue",
      headerName: t("inventoryStock.columns.totalValue"),
      width: 130,
      renderCell: (params) => <span className="total-value">₺{(params.value || 0).toFixed(2)}</span>,
    },
  ];

  const standardHelpItems = [
    t("inventoryStock.help.standard1"),
    t("inventoryStock.help.standard2"),
    t("inventoryStock.help.standard3"),
    t("inventoryStock.help.standard4"),
    t("inventoryStock.help.standard5"),
  ];

  const highlightedHelpItems = [
    t("inventoryStock.help.highlighted1"),
    t("inventoryStock.help.highlighted2"),
    t("inventoryStock.help.highlighted3"),
    t("inventoryStock.help.highlighted4"),
    t("inventoryStock.help.highlighted5"),
  ];

  return (
    /* <div className="inventory-stocks"> */

    /* <div className="stock-controls">
        <div className="action-buttons">
          <Button onClick={() => setShowStats(!showStats)} size="small">
            📊 İstatistikler
          </Button>
        </div>
      </div>

      
      {showStats && <StockStats />}
      <LowStockAlert /> */

    <LayoutMasterDetail
      itemType={itemType}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      hideCreateNewItem={!auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY]}
      SelectedItemComponent={StockTabs}
      NewItemComponent={InventoryStockItem}
      DeleteItem={DeleteStock}
      InsertItem={InsertStock}
      UpdateItem={UpdateStock}
      standardHelpItems={standardHelpItems}
      highlightedHelpItems={highlightedHelpItems}
      hideCreateNewItemWhenSelected={true}
      isAlwaysFullScreen={true}
    />
    /* </div> */
  );
};

export default InventoryStocks;
